import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AgGridTable, Pagination } from 'components/common';
import { useDispatch } from 'react-redux';
import { FaSearch, FaTimes } from 'react-icons/fa';
import CustomerLink from './component/CustomerLink';
import { getCustomerChecking } from 'redux/crm/customerSlice';
import { objectSerializer } from 'helpers/utils';
import { calPaginatorRecord } from 'helpers/paginator';
import { toast } from 'react-toastify';

const COLUMN_DEFS = [
  { headerName: 'Customer name', field: 'name',                   minWidth: 240, pinned: 'left', cellRenderer: CustomerLink },
  { headerName: 'ID#',           field: 'customer_code',          minWidth: 110, cellRenderer: CustomerLink },
  { headerName: 'Main contact',  field: 'main_contact',           minWidth: 170 },
  { headerName: 'Tax code',      field: 'tax_code',               minWidth: 150 },
  { headerName: 'Location',      field: 'sales_unit.description',  minWidth: 180 },
  { headerName: 'Created',       field: 'created_at',             minWidth: 170 },
  { headerName: 'Created by',    field: 'created_by',             minWidth: 140 }
];

const CustomerChecking = () => {
  const dispatch = useDispatch();
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState({
    data: [], totalRows: 0, pageSize: 20, totalPagesCount: 0,
    fromRecord: 0, toRecord: 0, currentPage: 1
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedFilter(filterValue), 500);
    return () => clearTimeout(handler);
  }, [filterValue]);

  const fetchCustomerChecking = useCallback(async (page = 1, pageSize = 20, filter = '') => {
    setLoading(true);
    try {
      const response = await dispatch(getCustomerChecking({ name_cont: filter, page, per_page: pageSize }));
      if (response.error) {
        toast.error(response.error);
        return;
      }
      const results = response.payload;
      const calRecords = calPaginatorRecord(results.currentPage || page, results.pageSize || pageSize, results.total_rows);

      setCustomers((prev) => ({
        ...prev,
        data: objectSerializer(results.rows),
        totalRows: results.total_rows,
        totalPagesCount: calRecords.totalPagesCount,
        fromRecord: calRecords.beginRecords || 1,
        toRecord: calRecords.lastRecords,
        currentPage: page,
        pageSize
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (debouncedFilter && debouncedFilter.length > 2) {
      fetchCustomerChecking(customers.currentPage, customers.pageSize, debouncedFilter);
    } else if (!debouncedFilter) {
      setCustomers((prev) => ({ ...prev, data: [], totalRows: 0, totalPagesCount: 0, fromRecord: 0, toRecord: 0 }));
    }
  }, [debouncedFilter, customers.currentPage, customers.pageSize, fetchCustomerChecking]);

  const onSearchChange = (e) => {
    setFilterValue(e.target.value);
    setCustomers((prev) => ({ ...prev, currentPage: 1 }));
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <Alert type="info" title="Quickly check customers in the system across all sales offices.">
        This function quickly checks who created and is responsible for the main customer contact to prevent duplicate creation.
      </Alert>

      {/* Search */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-p-3">
        <div className="tw-relative tw-flex tw-items-center">
          <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={14} />
          <input
            type="text"
            value={filterValue}
            onChange={onSearchChange}
            placeholder="Search name of customer…"
            className="tw-w-full tw-pl-9 tw-pr-9 tw-py-2.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
          />
          {filterValue && (
            <button
              type="button"
              onClick={() => setFilterValue('')}
              className="tw-absolute tw-right-3 tw-text-slate-400 hover:tw-text-slate-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
            >
              <FaTimes size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100">
          <h5 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0.5">Search results</h5>
          <p className="tw-text-sm tw-text-slate-500 tw-mb-0">
            We have found <span className="tw-font-bold tw-text-slate-700">{customers.totalRows || 0}</span> customer names.
          </p>
        </div>

        <AgGridTable
          rowData={customers.data}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          pagination={false}
          filter={false}
          sort
          rowHeight={38}
          headerHeight={42}
          fontSize="13px"
          height="max(320px, calc(100dvh - 360px))"
          enableCellTextSelection
        />

        {customers.totalPagesCount > 0 && (
          <div className="tw-px-3 tw-py-2 tw-border-t tw-border-slate-100">
            <Pagination
              currentPage={customers.currentPage}
              totalPages={customers.totalPagesCount}
              totalResults={customers.totalRows}
              perPage={customers.pageSize}
              pageSizeOptions={[10, 20, 50]}
              onPageChange={(page) => setCustomers((prev) => ({ ...prev, currentPage: page }))}
              onPageSizeChange={(size) => setCustomers((prev) => ({ ...prev, currentPage: 1, pageSize: size }))}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerChecking;
